import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { getAllProductsAsync, getProductsByCategoryAsync, searchProductsAsync } from '../productsSlice';
import { addToCartAsync } from '../../cart/cartSlice';
import { transformImageUrl } from '../../../utils/imageUtils';
import { categories as catalogCategories, getAllCatalogProducts, searchCatalogProducts } from '../../../data/productCatalog';
import ProductCard from '../../../components/ProductCard';

const Products = () => {
    const realProducts = useSelector((state) => state.products.products);
    const user = useSelector((state) => state.auth.user);
    const isLoggedIn = !!user;
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);
    const searchKeyword = queryParams.get('search');
    const category = queryParams.get('category');
    const categoryList = category ? category.split(',') : [];

    const noPlantsImage = "https://res.cloudinary.com/dcd6y2awx/image/upload/f_auto,q_auto/v1/PlantSeller/UI%20Images/no-data-found";

    // Handle Add to Cart
    const handleAddToCart = (item) => {
        const isFromCatalog = item.id && !item._id;

        if (isFromCatalog) {
            message.info('This is a catalog product. Check back soon for availability!');
            return;
        }

        if (!isLoggedIn) {
            message.warning('Please login to add items to cart');
            navigate('/login');
            return;
        }

        const quantity = 1;
        const discount = item.discount || 0;
        const priceWithoutDiscount = item.price * quantity;
        const priceAfterDiscount = Math.round(item.price - (discount / 100) * item.price) * quantity;
        const discountPrice = priceWithoutDiscount - priceAfterDiscount;

        const data = {
            user: user._id,
            nursery: item.nursery?._id || item.nursery,
            plant: item._id,
            quantity,
            pricing: {
                priceWithoutDiscount: priceWithoutDiscount.toFixed(2),
                priceAfterDiscount: priceAfterDiscount.toFixed(2),
                discount,
                discountPrice: discountPrice.toFixed(2),
            },
        };

        dispatch(addToCartAsync(data))
            .unwrap()
            .then(() => message.success(`${item.plantName || item.name} added to cart!`))
            .catch((error) => {
                if (error?.message?.includes('already') || error?.message?.includes('exists')) {
                    message.info('Item already in cart. Go to cart to update quantity.');
                } else {
                    message.error(error?.message || 'Failed to add to cart.');
                }
            });
    };

    // Merge real products with catalog products
    const products = useMemo(() => {
        if (realProducts && realProducts.length > 0) {
            // Normalize real products to match ProductCard format
            return realProducts.map((p) => ({
                ...p,
                id: p._id,
                name: p.plantName,
                image: transformImageUrl(p.images?.[0]?.url) || null,
                originalPrice: p.discount > 0 ? p.price : null,
                price: p.discount > 0 ? Math.round(p.price - (p.discount / 100) * p.price) : p.price,
                _originalPrice: p.price, // keep original for cart calc
                _discount: p.discount,
                inStock: (p.stock ?? 1) > 0,
                isReal: true,
            }));
        }

        // Fall back to catalog
        if (searchKeyword) {
            return searchCatalogProducts(searchKeyword);
        }
        if (category && category !== 'all') {
            const allCatalog = getAllCatalogProducts();
            return allCatalog.filter(
                (p) => categoryList.includes(p.category) || categoryList.some((c) => p.category.includes(c))
            );
        }
        return getAllCatalogProducts();
    }, [realProducts, searchKeyword, category]);

    useEffect(() => {
        if (!searchKeyword && !category) {
            dispatch(getAllProductsAsync());
        } else if (category && !searchKeyword) {
            if (categoryList.length === 1 && category === 'all') {
                dispatch(getAllProductsAsync());
            } else {
                dispatch(getProductsByCategoryAsync(category));
            }
        } else if (searchKeyword && !category) {
            dispatch(searchProductsAsync({ search: searchKeyword, category: null }));
        } else if (searchKeyword && category) {
            dispatch(searchProductsAsync({ search: searchKeyword, category }));
        }
    }, [location.search, dispatch]);

    const handleCategoryClick = (cat) => {
        if (cat === 'all' && categoryList.length === 0) {
            navigate('/products');
        } else {
            let query;
            if (cat === 'all') {
                query = 'all';
            } else if (categoryList.includes(cat)) {
                query = categoryList.filter((c) => c !== cat);
            } else {
                query = [...categoryList.filter((c) => c !== 'all'), cat];
            }
            navigate(`/products/?category=${query}`);
        }
    };

    const isActiveCategory = (cat) => {
        if (cat === 'all') return categoryList.length === 0 || categoryList.includes('all');
        return categoryList.includes(cat);
    };

    return (
        <div className="products-page-modern">
            <div className="products-page-inner">
                <h1 className="products-page-title">Our Collection</h1>
                <p className="products-page-subtitle">
                    Discover premium plants, seeds, and gardening essentials
                </p>

                {/* Category Filters */}
                <div className="category-pills">
                    {catalogCategories.map((cat) => (
                        <button
                            key={cat.key}
                            onClick={() => handleCategoryClick(cat.key)}
                            className={`category-pill ${isActiveCategory(cat.key) ? 'active' : ''}`}
                        >
                            <span className="pill-icon">{cat.icon}</span>
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Results Bar */}
                <div className="products-results-bar">
                    <span>
                        Showing {products.length} product{products.length !== 1 ? 's' : ''}
                        {searchKeyword && <> for &quot;{searchKeyword}&quot;</>}
                    </span>
                </div>

                {/* Products Grid */}
                {products.length > 0 ? (
                    <div className="products-grid-container">
                        {products.map((product) => (
                            <ProductCard
                                key={product.id || product._id}
                                product={product}
                                onAddToCart={() => handleAddToCart(product)}
                                linkTo={product.isReal ? `/product/${product._id}` : '#'}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="no-products-modern">
                        <img src={noPlantsImage} alt="No products found" />
                        <h3>No Products Found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                        <button
                            className="btn-outline-primary"
                            onClick={() => navigate('/products')}
                        >
                            <i className="fas fa-arrow-left"></i> View All Products
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Products;
